import { upload } from "@vercel/blob/client";
import { generateId } from "@scroll/shared-types";

export interface UploadedTimelineClip {
  clipId: string;
  blobKey: string;
  blobUrl: string;
}

export interface UploadedGreenScreenBackground {
  backgroundId: string;
  blobKey: string;
  blobUrl: string;
}

export interface UploadedTimelineAudio {
  audioId: string;
  blobKey: string;
  blobUrl: string;
}

export async function uploadTimelineClipFile(
  file: File,
  userId: string,
): Promise<UploadedTimelineClip> {
  if (!userId) throw new Error("userId required");

  const clipId = generateId();
  const pathname = `clips/${userId}/${clipId}.${timelineClipExtensionForFile(file)}`;
  const blob = await upload(pathname, file, {
    access: "public",
    handleUploadUrl: "/api/timeline-clips/handle-upload",
  });

  return { clipId, blobKey: blob.pathname, blobUrl: blob.url };
}

export async function uploadGreenScreenBackgroundFile(
  file: File,
  userId: string,
): Promise<UploadedGreenScreenBackground> {
  if (!userId) throw new Error("userId required");

  const backgroundId = generateId();
  const pathname = `clips/${userId}/green-screen/${backgroundId}.${greenScreenExtensionForFile(file)}`;
  const blob = await upload(pathname, file, {
    access: "public",
    handleUploadUrl: "/api/timeline-clips/handle-upload",
  });

  return { backgroundId, blobKey: blob.pathname, blobUrl: blob.url };
}

export async function uploadTimelineAudioFile(
  file: File,
  userId: string,
): Promise<UploadedTimelineAudio> {
  if (!userId) throw new Error("userId required");

  const audioId = generateId();
  const pathname = `clips/${userId}/audio/${audioId}.${audioExtensionForFile(file)}`;
  const blob = await upload(pathname, file, {
    access: "public",
    handleUploadUrl: "/api/timeline-clips/handle-upload",
  });

  return { audioId, blobKey: blob.pathname, blobUrl: blob.url };
}

// 404 and 409 are expected outcomes, not errors: the blob may already be
// gone, or the server refuses because a published post or an active publish
// pre-render still references it (the nightly sweep reclaims it later).
// Matches the Expo client (expo-app/lib/timelineUpload.ts).
export async function deleteTimelineClipBlob(fileName: string): Promise<void> {
  const res = await fetch(`/api/timeline-clips/${encodeURIComponent(fileName)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 404 && res.status !== 409) {
    throw new Error(`timeline clip delete failed (${res.status})`);
  }
}

export async function deleteGreenScreenBackgroundBlob(fileName: string): Promise<void> {
  const res = await fetch(`/api/timeline-clips/green-screen/${encodeURIComponent(fileName)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 404 && res.status !== 409) {
    throw new Error(`green screen background delete failed (${res.status})`);
  }
}

export async function deleteTimelineAudioBlob(fileName: string): Promise<void> {
  const res = await fetch(`/api/timeline-clips/audio/${encodeURIComponent(fileName)}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok && res.status !== 404 && res.status !== 409) {
    throw new Error(`timeline audio delete failed (${res.status})`);
  }
}

export async function cancelUploadedAudio(blobKey: string): Promise<void> {
  try {
    const fileName = timelineClipFileName(blobKey);
    await deleteTimelineAudioBlob(fileName);
  } catch (err) {
    console.warn("[timeline] cancelUploadedAudio failed (ignored):", blobKey, err);
  }
}

export function timelineClipFileName(blobKey: string): string {
  return blobKey.split("/").pop() ?? blobKey;
}

/**
 * Fire-and-forget wrapper around deleteTimelineClipBlob. Failures are logged
 * but never thrown — callers must not crash on blob-cleanup failure.
 */
export async function cancelUploadedClip(blobKey: string): Promise<void> {
  try {
    const fileName = timelineClipFileName(blobKey);
    await deleteTimelineClipBlob(fileName);
  } catch (err) {
    console.warn("[timeline] cancelUploadedClip failed (ignored):", blobKey, err);
  }
}

function audioExtensionForFile(file: File): "mp3" | "m4a" | "wav" | "ogg" {
  const contentType = file.type.toLowerCase();
  const name = file.name.toLowerCase().split("?")[0];
  if (contentType.includes("mpeg") || name.endsWith(".mp3")) return "mp3";
  if (contentType.includes("mp4") || contentType.includes("m4a") || name.endsWith(".m4a")) return "m4a";
  if (contentType.includes("wav") || name.endsWith(".wav")) return "wav";
  if (contentType.includes("ogg") || name.endsWith(".ogg")) return "ogg";
  return "mp3";
}

function greenScreenExtensionForFile(file: File): "jpg" | "png" | "webp" {
  const contentType = file.type.toLowerCase();
  const name = file.name.toLowerCase().split("?")[0];
  if (contentType.includes("png") || name.endsWith(".png")) return "png";
  if (contentType.includes("webp") || name.endsWith(".webp")) return "webp";
  return "jpg";
}

function timelineClipExtensionForFile(file: File): "mp4" | "jpg" | "png" | "webp" {
  const contentType = file.type.toLowerCase();
  if (contentType.startsWith("image/")) return greenScreenExtensionForFile(file);
  return "mp4";
}
